import { Delete } from "@mui/icons-material";
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	Tooltip,
} from "@mui/material";
import { FC, useEffect, useState } from "react";
import {
	createOrganization,
	emptyOrganizationInfo,
	Organization,
	OrganizationErrors,
	OrganizationInfo,
	updateOrganization,
} from "../../api/organizations";
import { OrganizationFields } from "./OrganizationFields";

type Props = {
	open: boolean;
	onClose: () => void;
	onSaved: (org: Organization) => void;
	/** When given, the dialog edits this organization instead of creating a new one. */
	organization?: Organization | null;
	/** Shown in edit mode only (site admins); should confirm before deleting. */
	onDelete?: (org: Organization) => void;
};

export const OrganizationDialog: FC<Props> = ({
	open,
	onClose,
	onSaved,
	organization,
	onDelete,
}) => {
	const [info, setInfo] = useState<OrganizationInfo>(emptyOrganizationInfo);
	const [errors, setErrors] = useState<OrganizationErrors>({});
	const [submitting, setSubmitting] = useState(false);

	useEffect(() => {
		if (open) {
			setInfo(
				organization
					? {
							name: organization.name,
							address: organization.address,
							phone: organization.phone,
							url: organization.url,
						}
					: emptyOrganizationInfo,
			);
			setErrors({});
		}
	}, [open, organization?.id]);

	const handleSubmit = async () => {
		setSubmitting(true);
		try {
			const result = organization
				? await updateOrganization(organization.id, info)
				: await createOrganization(info);
			if (result.errors) {
				setErrors(result.errors);
			} else if (result.organization) {
				onSaved(result.organization);
				onClose();
			}
		} catch (e) {
			// request interceptor already surfaces the error message
			console.error(e);
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
			<DialogTitle>
				{organization ? "Edit Organization" : "Add New Organization"}
			</DialogTitle>
			<DialogContent>
				<OrganizationFields value={info} onChange={setInfo} errors={errors} />
			</DialogContent>
			<DialogActions>
				{organization && onDelete && (
					<Tooltip title="Delete organization">
						<IconButton onClick={() => onDelete(organization)}>
							<Delete color="error" />
						</IconButton>
					</Tooltip>
				)}
				<Box sx={{ flex: 1 }} />
				<Button onClick={onClose}>Cancel</Button>
				<Button
					variant="contained"
					onClick={handleSubmit}
					disabled={submitting || info.name.trim().length === 0}
				>
					{organization ? "Save" : "Add"}
				</Button>
			</DialogActions>
		</Dialog>
	);
};
